// Link próprio do parceiro para captar clientes (#6511).
//
// Cada parceiro e vendedor parceiro tem um código (profiles.codigo_indicacao). O mesmo
// código serve a dois links:
//   - #/cadastro-consultor/<código> recruta vendedor parceiro para a rede (já existia);
//   - #/parceiro/<código> capta cliente: a pessoa deixa nome, WhatsApp, cidade e conta
//     de luz, e o lead nasce no CRM com o parceiro como responsável, fora do rodízio.
// O lead entra pela mesma porta do site (tabela pre_venda), com o código em
// `indicado_por`. Quem decide o dono é o banco (fn_pre_venda_criar_negociacao,
// migration 20261029200000): código inválido ou de parceiro inativo vira lead comum
// do site, no rodízio — o cliente nunca fica sem atendimento.

// A negociação nasce com origem "Link do parceiro" (ORIGEM_LINK_PARCEIRO, em origensNegociacao.ts).

/** O que a página grava em pre_venda.origem, para separar do formulário do site. */
export const ORIGEM_PRE_VENDA_LINK = 'link_parceiro';

/** Os mesmos tipos de conta do simulador do site (pre_venda.tipo_conta). */
export const TIPOS_CONTA = ['Residência', 'Empresa', 'Condomínio'] as const;
export type TipoConta = (typeof TIPOS_CONTA)[number];

/** Código como o banco guarda: maiúsculo, sem espaço. Inválido → null. */
export function normalizarCodigo(codigo: string | null | undefined): string | null {
  const c = (codigo ?? '').trim().toUpperCase();
  return /^[A-Z0-9-]{4,20}$/.test(c) ? c : null;
}

/** Link que o parceiro compartilha. `base` é a origem + caminho do app (sem o "#"). */
export function linkDeCaptacao(base: string, codigo: string): string {
  return `${base}#/parceiro/${encodeURIComponent(codigo)}`;
}

/** Só os dígitos do telefone, sem o 55 do Brasil (mesma regra do banco). */
export function digitosWhatsapp(texto: string): string {
  const d = (texto ?? '').replace(/\D/g, '');
  return d.length >= 12 && d.startsWith('55') ? d.slice(2) : d;
}

/** "R$ 1.234,56", "450", "450,5" → número. Vazio, zero ou ilegível → null. */
export function lerValorConta(texto: string): number | null {
  const limpo = (texto ?? '').replace(/[^\d,.]/g, '');
  if (!limpo) return null;
  // Vírgula é o decimal; ponto antes dela é milhar. Sem vírgula, ponto seguido de
  // 3 dígitos também é milhar ("1.234"), e de 1–2 dígitos é decimal ("450.5").
  let normal: string;
  if (limpo.includes(',')) normal = limpo.replace(/\./g, '').replace(',', '.');
  else if (/\.\d{3}(\.|$)/.test(limpo)) normal = limpo.replace(/\./g, '');
  else normal = limpo;
  const v = Number(normal);
  return Number.isFinite(v) && v > 0 ? Math.round(v * 100) / 100 : null;
}

export interface DadosDoLead {
  nome: string;
  whatsapp: string;
  cidade: string;
  tipo: TipoConta | '';
  valorConta: string;
}

/** O que falta para enviar, na ordem do formulário. Tudo certo → null. */
export function faltaNoLead(d: DadosDoLead): string | null {
  if (d.nome.trim().split(/\s+/).filter(Boolean).length < 2) return 'Escreva seu nome e sobrenome.';
  const tel = digitosWhatsapp(d.whatsapp);
  if (tel.length < 10 || tel.length > 11) return 'Confira o WhatsApp: DDD e número.';
  if (d.cidade.trim().length < 2) return 'Escreva sua cidade.';
  if (!d.tipo) return 'Escolha onde é a conta de luz.';
  if (lerValorConta(d.valorConta) == null) return 'Escreva o valor médio da sua conta de luz.';
  return null;
}

/** A linha que vai para pre_venda. O banco cria a negociação no gatilho. */
export function linhaPreVenda(d: DadosDoLead, codigo: string | null) {
  return {
    nome_completo: d.nome.trim().replace(/\s+/g, ' '),
    whatsapp: digitosWhatsapp(d.whatsapp),
    cidade: d.cidade.trim(),
    tipo_conta: d.tipo || null,
    valor_conta_luz: lerValorConta(d.valorConta),
    origem: ORIGEM_PRE_VENDA_LINK,
    indicado_por: codigo,
    status: 'novo',
  };
}

/** Texto pronto para o parceiro mandar no WhatsApp. */
export function mensagemDoLink(link: string): string {
  return `Quer pagar menos na conta de luz? Com a energia solar por assinatura da Vigor Energy você não precisa de obra nem de instalar nada. Deixe seus dados neste link que eu te chamo: ${link}`;
}
